import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import SidebarPageLayout from '../../components/layout/SidebarPageLayout';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { useAuth } from '../../components/auth/AuthProvider';
import { fetchCmsUser, updateCmsUser } from '../../api/auth';
import { appRoleById, canViewUsers, isSuperAdminRole } from '../../auth/appRoles';

function displayValue(value) {
  const text = String(value ?? '').trim();
  return text || '—';
}

function formatTimestamp(value, withTime) {
  if (value == null || value === '') {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const options = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };
  if (withTime) {
    options.hour = 'numeric';
    options.minute = '2-digit';
  }
  return parsed.toLocaleString('en-US', options);
}

function UserProfile() {
  const { userId = '' } = useParams();
  const { user, isAuthLoading, patchUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [roles, setRoles] = useState([]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [siteIds, setSiteIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const allowed = canViewUsers(user?.role_id);

  useEffect(() => {
    document.title = 'Vivosol Plant Performance Insights Hub | Admin / User';
  }, []);

  useEffect(() => {
    if (isAuthLoading || !allowed) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setDenied(false);
      setSaved(false);
      setSaveError('');
      try {
        const response = await fetchCmsUser(userId);
        const next = response?.user || null;
        if (!cancelled) {
          if (!next) {
            setProfile(null);
            setDenied(true);
            return;
          }
          setProfile(next);
          setRoles(Array.isArray(response?.roles) ? response.roles : []);
          setFirstName(String(next.first_name ?? ''));
          setLastName(String(next.last_name ?? ''));
          setRoleId(next.role_id == null ? '' : String(next.role_id));
          setSiteIds(
            (Array.isArray(next.sites) ? next.sites : [])
              .filter((site) => site?.granted)
              .map((site) => String(site.site_id)),
          );
        }
      } catch (error) {
        console.warn('Failed to open user profile:', error);
        if (!cancelled) {
          setProfile(null);
          setDenied(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [allowed, isAuthLoading, userId]);

  async function handleUpdateProfile(event) {
    event.preventDefault();
    setSaveError('');
    setSaved(false);

    const accountSites = Array.isArray(profile?.sites) ? profile.sites : [];
    if (accountSites.length > 0 && siteIds.length === 0) {
      setSaveError('Check at least one site.');
      return;
    }

    setSaving(true);

    try {
      const data = await updateCmsUser(userId, {
        firstName,
        lastName,
        roleId: isSuperAdminRole(profile?.role_id) ? profile?.role_id : roleId,
        siteIds: siteIds.map((id) => Number(id)),
      });
      const next = data?.user;
      if (next) {
        setProfile((current) => ({ ...current, ...next }));
        setFirstName(String(next.first_name ?? ''));
        setLastName(String(next.last_name ?? ''));
        if (next.role_id != null) {
          setRoleId(String(next.role_id));
        }
        if (Array.isArray(next.sites)) {
          setSiteIds(next.sites.filter((site) => site?.granted).map((site) => String(site.site_id)));
        }
        if (user?.email && next.email && user.email.toLowerCase() === String(next.email).toLowerCase()) {
          patchUser({
            first_name: next.first_name,
            last_name: next.last_name,
            role_id: next.role_id,
          });
        }
      }
      setSaved(true);
    } catch (error) {
      console.warn('User profile update failed:', error);
      setSaveError(error?.message === 'Check at least one site.' ? error.message : 'Unable to update profile.');
    } finally {
      setSaving(false);
    }
  }

  if (!isAuthLoading && !allowed) {
    return <Navigate to="/admin/account" replace />;
  }

  if (!isLoading && denied) {
    return <Navigate to="/admin/users" replace />;
  }

  const profileName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim();
  const organization = String(profile?.account_name ?? '').trim();
  const targetIsSuper = isSuperAdminRole(profile?.role_id);
  const superRole = appRoleById(1);
  const createdLabel = formatTimestamp(profile?.created_at, false);
  const lastLoginLabel = formatTimestamp(profile?.last_login, true);
  const siteRecords = Array.isArray(profile?.sites) ? profile.sites : [];

  return (
    <SidebarPageLayout id="adminUser">
      <div className="content p-5">
        <h1 className="mb-0">{profileName || 'User'}</h1>
        <Breadcrumbs />
        <div className="panel p-3">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faUser} /> PROFILE</span>
            </div>
          </div>
          <div className="body pt-3">
            {isAuthLoading || isLoading ? (
              <p className="text-muted mb-0">Loading profile…</p>
            ) : (
              <form className="form account-profile d-flex flex-column" onSubmit={handleUpdateProfile}>
                {saveError ? (
                  <p className="account-save-error account-profile-error mb-0" role="alert">{saveError}</p>
                ) : null}
                <div className="form-group">
                  <span className="form-label">Email</span>
                  <div className="form-control-plaintext account-email">{displayValue(profile?.email)}</div>
                </div>
                <div className="form-group">
                  <span className="form-label">First Name</span>
                  <input
                    type="text"
                    className="form-control"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    aria-label="First Name"
                  />
                </div>
                <div className="form-group">
                  <span className="form-label">Last Name</span>
                  <input
                    type="text"
                    className="form-control"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    aria-label="Last Name"
                  />
                </div>
                <div className="form-group">
                  <span className="form-label">Organization</span>
                  <div className="form-control-plaintext account-organization">
                    {displayValue(organization)}
                  </div>
                </div>
                <div className="form-group">
                  <span className="form-label">Role</span>
                  <div className="form-control-plaintext account-role">
                    {targetIsSuper ? (
                      superRole ? <Link to="/help/documentation/roles">{superRole.label}</Link> : '—'
                    ) : (
                      <select
                        className="form-select account-role-select"
                        aria-label="Role"
                        value={roleId}
                        onChange={(event) => setRoleId(event.target.value)}
                      >
                        {roles.map((role) => (
                          <option key={role.role_id} value={String(role.role_id)}>{role.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                <div className="form-group account-sites-group">
                  <span className="form-label">Sites</span>
                  <div className="account-site-list">
                    {siteRecords.length ? siteRecords.map((site) => {
                      const id = String(site.site_id);
                      const siteName = String(site?.name || '').trim();
                      return (
                        <label key={id} className="account-site-check">
                          <input
                            type="checkbox"
                            checked={siteIds.includes(id)}
                            onChange={() => {
                              setSiteIds((current) => (
                                current.includes(id)
                                  ? current.filter((value) => value !== id)
                                  : [...current, id]
                              ));
                            }}
                          />
                          <span>{siteName || '—'}</span>
                        </label>
                      );
                    }) : '—'}
                  </div>
                </div>
                <div className="form-group">
                  <span className="form-label">Last Login</span>
                  <div className="form-control-plaintext account-last-login">{lastLoginLabel || '—'}</div>
                </div>
                <div className="form-group">
                  <span className="form-label">Created</span>
                  <div className="form-control-plaintext account-created">{createdLabel || '—'}</div>
                </div>
                <div className="form-group account-actions">
                  <span className="form-label" aria-hidden="true" />
                  <div className="account-update-wrap">
                    <button type="submit" className="account-update" disabled={saving}>
                      Update Profile
                    </button>
                    {saving ? (
                      <span className="account-update-spinner" role="status" aria-label="Updating profile" />
                    ) : null}
                    {saved ? <p className="account-save-ok mb-0">Profile Updated</p> : null}
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default UserProfile;
